import React from 'react';

export interface CCSorteoBasic {
  id: string;
  nombre: string;
  tipo_id: string;
  estado: string;
  hora_cierre: string | null;
}

function formatHoraCierre(hora: string | null | undefined): string {
  if (!hora) return '—';
  const [h, m] = hora.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hora;
  return new Date(2026, 0, 1, h, m).toLocaleTimeString('es-CR', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function tiempoRestante(hora: string | null | undefined): string | null {
  if (!hora) return null;
  const [h, m, s] = hora.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const ahora = new Date();
  const cierre = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), h, m, s || 0);
  const diff = cierre.getTime() - ahora.getTime();
  if (diff <= 0) return 'Cerrado';
  const minutos = Math.floor(diff / 60000);
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  return horas > 0 ? `${horas}h ${mins}m` : `${mins}m`;
}

interface SorteoCardProps {
  sorteos: CCSorteoBasic[];
  sorteoId: string;
  onSorteoChange: (id: string) => void;
}

export const SorteoCard: React.FC<SorteoCardProps> = ({ sorteos, sorteoId, onSorteoChange }) => {
  const selected = sorteos.find((s) => s.id === sorteoId);
  const esReventado = selected?.tipo_id === 'REVENTADO';
  const restante = selected ? tiempoRestante(selected.hora_cierre) : null;

  if (sorteos.length === 0) {
    return (
      <div className="bg-gray-900 rounded-2xl border border-gray-800 px-4 py-4">
        <p className="text-gray-400 text-sm text-center">No hay sorteos abiertos en este momento.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
      {/* Sorteo selector */}
      <div className="px-4 py-1 border-b border-gray-800">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">Sorteo</p>
      </div>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {sorteos.length === 1 ? (
              <p className="text-white font-bold text-lg">{selected?.nombre}</p>
            ) : (
              <select
                value={sorteoId}
                onChange={(e) => onSorteoChange(e.target.value)}
                className="bg-transparent text-white font-bold text-lg w-full focus:outline-none appearance-none cursor-pointer"
              >
                {sorteos.map((s) => (
                  <option key={s.id} value={s.id} className="bg-gray-900">
                    {s.nombre}
                  </option>
                ))}
              </select>
            )}
            <p className="text-gray-400 text-xs mt-0.5">
              Cierra {formatHoraCierre(selected?.hora_cierre)}
            </p>
          </div>
          {sorteos.length > 1 && (
            <span className="text-gray-500 text-xl ml-2">▾</span>
          )}
        </div>

        {/* Status badges */}
        <div className="flex items-center gap-2 mt-2">
          <span className={`text-xs font-bold ${selected?.estado === 'ABIERTO' ? 'text-green-400' : 'text-red-400'}`}>
            ● {selected?.estado === 'ABIERTO' ? 'Abierto' : 'Cerrado'}
          </span>
          {esReventado && (
            <span className="text-[10px] bg-purple-900/80 text-purple-200 px-2 py-0.5 rounded-full font-bold uppercase">
              Reventado
            </span>
          )}
          {restante && restante !== 'Cerrado' && (
            <span className="text-xs text-gray-500 ml-auto flex items-center gap-1">
              ⏱ {restante}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
